import React, { useState, useMemo, useCallback, useRef, useEffect, lazy, Suspense } from 'react';
import { useServerData } from './hooks/useServerData';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { useStockData, useMultipleQuotes, useCandles, useSymbolSearch } from './hooks/useStockData';

// Constants & Utils
import { LS_KEYS, MAX_CHAT_PERSIST, RISK_PROFILES, SECTORS, PROFILE_QUESTIONS } from './constants';
import { migrateStorage } from './utils/storage';
import { calcRisk } from './utils/helpers';
import { calculateRSI, calculateMACD, calculateSMA, getSignal } from './utils/technicalAnalysis';
import { callClaude, fmtProfile, fmtCompactPortfolio, fmtPortfolioSnapshot, fmtAlertSummary } from './services/claudeApi';
import { computeAlerts } from './utils/alertEngine';
import { useToast } from './components/common/Toast';

// Eager components (always visible)
import MigrationPrompt from './components/MigrationPrompt';
import Header from './components/layout/Header';
import TabBar from './components/layout/TabBar';
import WatchlistSidebar from './components/layout/WatchlistSidebar';
import DashboardTab from './components/DashboardTab';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import { PortfolioProvider } from './contexts/PortfolioContext';

// Lazy-loaded tabs (loaded on first visit)
const AnalysisTab = lazy(() => import('./components/AnalysisTab'));
const AlertsTab = lazy(() => import('./components/AlertsTab'));
const AnalystTab = lazy(() => import('./components/AnalystTab'));
const PortfolioTab = lazy(() => import('./components/portfolio/PortfolioTab'));
const MacroTab = lazy(() => import('./components/MacroTab'));
const AdminCockpit = lazy(() => import('./components/admin/AdminCockpit'));
const ApiMonitorTab = lazy(() => import('./components/ApiMonitorTab'));
const AccountTab = lazy(() => import('./components/AccountTab'));
const TradeJournalTab = lazy(() => import('./components/journal/TradeJournalTab'));

// Suspense fallback — minimal spinner
const TabLoader = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 size={24} className="animate-spin text-txt-muted" />
  </div>
);

// ═══════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════
// Run storage migration before component renders
migrateStorage();

const StockMonitor = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ── Dashboard State (synced with Worker KV via useServerData) ──
  const [watchlist, setWatchlist] = useServerData('watchlist', 'personal', ['AAPL','NVDA','MSFT','TSLA'], 'sm_watchlist');
  const [selectedStock, setSelectedStock] = useState('AAPL');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddStock, setShowAddStock] = useState(false);
  const [chartDays, setChartDays] = useState(30);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [migrationDismissed, setMigrationDismissed] = useState(() => localStorage.getItem('sm_migration_dismissed') === '1');

  // ── Advisor State (synced with Worker KV) ──
  const [advisorProfile, setAdvisorProfile] = useServerData('profile', 'personal', null, LS_KEYS.PROFILE);
  const [profileStep, setProfileStep] = useState(0);
  const [profileAnswers, setProfileAnswers] = useState({});
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [portfolio, setPortfolio] = useServerData('portfolio_positions', 'personal', [], LS_KEYS.PORTFOLIO);
  const [advisorChat, setAdvisorChat] = useServerData('analyst_chat', 'personal', [], LS_KEYS.CHAT);
  const [advisorInput, setAdvisorInput] = useState("");
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const chatEndRef = useRef(null);

  // ── Data Hooks ──
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [advisorChat, advisorLoading]);

  const { data: stockData, loading: stockLoading, error: stockError, refetch } = useStockData(selectedStock);
  // Fetch quotes for both watchlist AND portfolio stocks in a single batch
  const allQuoteSymbols = useMemo(() => {
    const pSymbols = (portfolio || []).map(p => p.symbol).filter(Boolean);
    return [...new Set([...watchlist, ...pSymbols])];
  }, [watchlist, portfolio]);
  const { quotes: watchlistQuotes } = useMultipleQuotes(allQuoteSymbols, 30000);
  // Single fetch: always 300 days (needed for SMA 50/200), then slice for chart display
  const { candles: allCandles, loading: candlesLoading } = useCandles(selectedStock, 'D', 300);
  const candles = useMemo(() => allCandles ? allCandles.slice(-chartDays) : null, [allCandles, chartDays]);
  const { results: searchResults, loading: searchLoading } = useSymbolSearch(searchTerm);

  // ── Derived Data ──
  const currentPrice = stockData?.quote?.c || 0;
  const priceChange = stockData?.quote?.d || 0;
  const priceChangePercent = stockData?.quote?.dp || 0;
  const companyName = stockData?.profile?.name || selectedStock;
  const exchange = stockData?.profile?.exchange || '';
  const logo = stockData?.profile?.logo || '';
  const industry = stockData?.profile?.finnhubIndustry || '';
  const isin = stockData?.profile?.isin || '';
  const chartData = useMemo(() => candles || [], [candles]);
  const smaData = useMemo(() => allCandles || [], [allCandles]);
  const rsi = useMemo(() => calculateRSI(chartData), [chartData]);
  const macdData = useMemo(() => calculateMACD(chartData), [chartData]);
  const macd = macdData.macd;
  const macdSignal = macdData.signal;
  const macdHistogram = macdData.histogram;
  const signal = useMemo(() => getSignal(rsi, macd), [rsi, macd]);

  const volatility = useMemo(() => {
    if (!chartData || chartData.length < 2) return 0;
    const returns = chartData.slice(1).map((d, i) => Math.log(d.price / chartData[i].price));
    const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((a, r) => a + Math.pow(r - mean, 2), 0) / returns.length;
    return (Math.sqrt(variance) * Math.sqrt(252) * 100).toFixed(2);
  }, [chartData]);

  const sma50 = useMemo(() => calculateSMA(smaData, 50), [smaData]);
  const sma200 = useMemo(() => calculateSMA(smaData, 200), [smaData]);

  // Precompute alerts for selectedStock (used by Alert Scanner skill)
  const currentAlerts = useMemo(() =>
    computeAlerts(selectedStock, { rsi, macd, priceChangePercent, volatility, currentPrice, sma50, sma200 }),
    [selectedStock, rsi, macd, priceChangePercent, volatility, currentPrice, sma50, sma200]
  );

  // ── Dashboard Actions ──
  const addToWatchlist = (symbol) => { if (!watchlist.includes(symbol)) { setWatchlist([...watchlist, symbol]); toast.success(`${symbol} aggiunto alla watchlist`); } setShowAddStock(false); setSearchTerm(''); };
  const removeFromWatchlist = (symbol) => { setWatchlist(watchlist.filter(s => s !== symbol)); if (selectedStock === symbol && watchlist.length > 1) setSelectedStock(watchlist.find(s => s !== symbol)); toast.info(`${symbol} rimosso dalla watchlist`); };

  // ── Advisor Actions ──
  const handleProfileAnswer = (ans) => {
    const newA = { ...profileAnswers, [PROFILE_QUESTIONS[profileStep].key]: ans };
    setProfileAnswers(newA);
    if (profileStep < PROFILE_QUESTIONS.length - 1) setProfileStep(profileStep + 1);
  };

  const toggleSector = (id) => {
    if (id === "none") { setSelectedSectors(["none"]); return; }
    setSelectedSectors(prev => { const w = prev.filter(s => s !== "none"); return w.includes(id) ? w.filter(s => s !== id) : [...w, id]; });
  };

  const confirmSectors = () => {
    const labels = selectedSectors.includes("none") ? "Nessuna preferenza" : selectedSectors.map(id => SECTORS.find(s => s.id === id)?.label).filter(Boolean).join(", ");
    const newA = { ...profileAnswers, sectors: labels };
    setProfileAnswers(newA);
    const rt = calcRisk(newA);
    setAdvisorProfile({ ...newA, riskType: rt, ...RISK_PROFILES[rt] });
  };

  const resetProfile = () => { setAdvisorProfile(null); setProfileStep(0); setProfileAnswers({}); setSelectedSectors([]); };

  const clearChat = () => { setAdvisorChat([]); };

  const sendAdvisorMsg = useCallback(async () => {
    const q = advisorInput.trim();
    if (!q || advisorLoading) return;
    setAdvisorInput("");
    setAdvisorChat(prev => [...prev, { role: "user", text: q }].slice(-MAX_CHAT_PERSIST));
    setAdvisorLoading(true);
    const history = advisorChat.slice(-10).map(m => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
    history.push({ role: "user", content: q });
    // Read funds from localStorage to include in context (funds are managed by PortfolioContext)
    let funds = [];
    try { const raw = localStorage.getItem(LS_KEYS.FUNDS); if (raw) funds = JSON.parse(raw); } catch { /* ignore */ }
    const snapshot = fmtPortfolioSnapshot(portfolio, funds, watchlistQuotes);
    const alertCtx = fmtAlertSummary(currentAlerts);
    const result = await callClaude(history, fmtProfile(advisorProfile), fmtCompactPortfolio(portfolio, funds, watchlistQuotes), snapshot, alertCtx);
    if (result.error) toast.error('Errore nella risposta AI');
    setAdvisorChat(prev => [...prev, { role: "assistant", text: result.error ? `❌ Errore: ${result.error}` : result.text }].slice(-MAX_CHAT_PERSIST));
    setAdvisorLoading(false);
  }, [advisorInput, advisorLoading, advisorChat, advisorProfile, portfolio, toast, setAdvisorChat, watchlistQuotes, currentAlerts]);

  // ═══════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════
  const isMarketTab = ['dashboard', 'analysis', 'alerts'].includes(activeTab);

  return (
    <div className="min-h-screen bg-base text-txt-primary p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <Header onMenuToggle={() => setSidebarOpen(s => !s)} />
        {!migrationDismissed && <MigrationPrompt onDismiss={() => { localStorage.setItem('sm_migration_dismissed', '1'); setMigrationDismissed(true); }} />}
        <TabBar activeTab={activeTab} setActiveTab={setActiveTab} advisorProfile={advisorProfile} portfolioCount={portfolio.length} />

        {isMarketTab ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <WatchlistSidebar
              isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
              watchlist={watchlist} selectedStock={selectedStock} setSelectedStock={setSelectedStock}
              showAddStock={showAddStock} setShowAddStock={setShowAddStock}
              searchTerm={searchTerm} setSearchTerm={setSearchTerm}
              searchResults={searchResults} searchLoading={searchLoading}
              watchlistQuotes={watchlistQuotes}
              addToWatchlist={addToWatchlist} removeFromWatchlist={removeFromWatchlist}
            />
            <div className="lg:col-span-3 space-y-6">
              {stockError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                  <AlertCircle className="text-red-400" size={20} />
                  <div className="flex-1">
                    <div className="text-red-400 font-medium text-sm">Errore caricamento dati</div>
                    <div className="text-xs text-txt-secondary">{stockError}</div>
                  </div>
                  <button onClick={refetch} className="p-2 hover:bg-elevated rounded-lg"><RefreshCw size={16} /></button>
                </div>
              )}
              <ErrorBoundary>
                <Suspense fallback={<TabLoader />}>
                {activeTab === 'dashboard' && (
                  <DashboardTab selectedStock={selectedStock} stockLoading={stockLoading} stockData={stockData}
                    currentPrice={currentPrice} priceChange={priceChange} priceChangePercent={priceChangePercent}
                    companyName={companyName} exchange={exchange} logo={logo} industry={industry} isin={isin} signal={signal} refetch={refetch}
                    chartData={chartData} chartDays={chartDays} setChartDays={setChartDays} candlesLoading={candlesLoading} />
                )}
                {activeTab === 'analysis' && (
                  <AnalysisTab selectedStock={selectedStock} rsi={rsi} macd={macd} macdSignal={macdSignal} macdHistogram={macdHistogram} volatility={volatility} chartData={chartData} chartDays={chartDays} currentPrice={currentPrice} sma50={sma50} sma200={sma200} />
                )}
                {activeTab === 'alerts' && (
                  <AlertsTab selectedStock={selectedStock} rsi={rsi} macd={macd} priceChangePercent={priceChangePercent} volatility={volatility} currentPrice={currentPrice} sma50={sma50} sma200={sma200} />
                )}
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-6">
            <ErrorBoundary>
            <Suspense fallback={<TabLoader />}>
            {activeTab === 'advisor' && (
              <AnalystTab
                advisorProfile={advisorProfile} profileStep={profileStep} setProfileStep={setProfileStep}
                profileAnswers={profileAnswers} selectedSectors={selectedSectors}
                handleProfileAnswer={handleProfileAnswer} toggleSector={toggleSector} confirmSectors={confirmSectors} resetProfile={resetProfile}
                advisorChat={advisorChat} advisorInput={advisorInput} setAdvisorInput={setAdvisorInput} advisorLoading={advisorLoading}
                sendAdvisorMsg={sendAdvisorMsg} clearChat={clearChat} chatEndRef={chatEndRef}
                watchlist={watchlist} portfolio={portfolio} />
            )}
            {activeTab === 'portfolio' && (
              <PortfolioProvider advisorProfile={advisorProfile} watchlistQuotes={watchlistQuotes} portfolio={portfolio} setPortfolio={setPortfolio}>
                <PortfolioTab />
              </PortfolioProvider>
            )}
            {activeTab === 'macro' && (
              <MacroTab />
            )}
            {activeTab === 'journal' && (
              <TradeJournalTab toast={toast} />
            )}
            {activeTab === 'monitor' && (
              isAdmin ? <AdminCockpit toast={toast} /> : <ApiMonitorTab />
            )}
            {activeTab === 'account' && (
              <AccountTab toast={toast} />
            )}
            </Suspense>
            </ErrorBoundary>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockMonitor;